/**
 * The network half of sync — `SyncTransport` over `fetch`.
 *
 * This is the other side of the seam ADR-0001 §3.1 draws: `@rush/sync-core` may not import
 * `fetch`, so the engine talks to an interface and this file is the one implementation of
 * it that touches the network.
 *
 * **The job here is smaller and stricter than it looks.** `OutboxDrainer` will mark an op
 * `synced` — permanently discarding our obligation to resend somebody's evidence — on the
 * strength of whatever this function returns. So this file's real responsibility is not
 * "make an HTTP call", it is: *never hand the drainer a confirmation the server did not
 * actually make.* Every ambiguous outcome therefore throws, because a throw is the one
 * response the drainer handles by changing nothing (invariant 4) and resending later.
 *
 * Things that throw rather than resolve:
 *
 *  - any non-2xx, including 4xx. A 400 on the envelope is not a per-op rejection and must
 *    not be read as one.
 *  - a body that is not JSON, or is JSON of the wrong shape.
 *  - a `protocolVersion` we do not speak.
 *  - a result entry missing the field its own status requires — an `applied` with no `seq`
 *    is not a confirmation, it is a bug, and treating it as one would mark evidence synced
 *    against a sequence number that does not exist.
 *
 * A resend is free; `opId` carries a unique index server-side and a duplicate comes back as
 * `duplicate`, which is a success. There is no symmetric recovery from marking an op synced
 * that never landed, so every judgement call in this file goes the same way.
 */

import {
  PROTOCOL_VERSION,
  type OpError,
  type OpErrorCode,
  type OpResult,
  type PullChangesRequest,
  type PullChangesResponse,
  type PushOpsRequest,
  type PushOpsResponse,
  type SyncTransport,
  type Uuid,
} from '@rush/sync-core';

/** Injected so tests do not need a network or a fetch polyfill. */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface HttpSyncTransportOptions {
  readonly baseUrl: string;
  readonly fetch?: FetchLike;
  /**
   * Per-request ceiling.
   *
   * Generous on purpose. A crew on one bar of LTE pushing a batch is slow, not broken, and
   * a tight timeout turns a working upload into a retry loop that never finishes a batch.
   */
  readonly timeoutMs?: number;
  /** Extra headers — the auth header, once there is authentication. */
  readonly headers?: Readonly<Record<string, string>>;
}

const DEFAULT_TIMEOUT_MS = 30_000;

export class HttpSyncTransport implements SyncTransport {
  readonly #baseUrl: string;
  readonly #fetch: FetchLike;
  readonly #timeoutMs: number;
  readonly #headers: Readonly<Record<string, string>>;

  constructor(options: HttpSyncTransportOptions) {
    // Trailing slash normalised once here so every call site can use a leading-slash path
    // and nobody has to remember which half owns the separator.
    this.#baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.#fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.#timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.#headers = options.headers ?? {};
  }

  async pushOps(request: PushOpsRequest): Promise<PushOpsResponse> {
    const body = await this.#send('/sync/ops', {
      method: 'POST',
      body: JSON.stringify(request),
      headers: { 'content-type': 'application/json' },
    });

    const protocolVersion = readProtocolVersion(body);
    const rawResults = readArray(body, 'results');

    return {
      protocolVersion,
      results: rawResults.map((entry, index) => parseOpResult(entry, index)),
    };
  }

  async pullChanges(request: PullChangesRequest): Promise<PullChangesResponse> {
    const query = `?since=${String(request.since)}&limit=${String(request.limit)}`;
    const body = await this.#send(`/sync/changes${query}`, { method: 'GET' });

    const protocolVersion = readProtocolVersion(body);
    const changes = readArray(body, 'changes');
    const latestSeq = body['latestSeq'];
    if (typeof latestSeq !== 'number' || !Number.isFinite(latestSeq)) {
      throw new SyncProtocolError('pullChanges response has no usable latestSeq cursor');
    }

    return {
      protocolVersion,
      // Change entries are pass-through for now: nothing consumes a pull yet, and
      // validating a shape before there is a reader for it invents rules the server has not
      // agreed to. The push path is the one that can destroy evidence, so it is the one
      // that is strict.
      changes: changes as PullChangesResponse['changes'],
      latestSeq,
      hasMore: body['hasMore'] === true,
    };
  }

  async #send(path: string, init: RequestInit): Promise<Record<string, unknown>> {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
    }, this.#timeoutMs);

    let response: Response;
    try {
      response = await this.#fetch(`${this.#baseUrl}${path}`, {
        ...init,
        signal: controller.signal,
        headers: { ...this.#headers, ...(init.headers as Record<string, string> | undefined) },
      });
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      // Deliberately one branch for 4xx and 5xx. The drainer's terminal-vs-retryable
      // decision is made per op, from an `OpError` the server sent about that op. An HTTP
      // status is about the request, and inferring "this evidence is permanently rejected"
      // from a 400 on the envelope would quarantine an entity over a routing mistake.
      throw new SyncTransportError(
        `sync request failed: ${String(response.status)} ${response.statusText}`,
        response.status,
      );
    }

    let parsed: unknown;
    try {
      parsed = await response.json();
    } catch {
      throw new SyncProtocolError('sync response was not JSON');
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new SyncProtocolError('sync response was not a JSON object');
    }

    return parsed as Record<string, unknown>;
  }
}

/** The request never reached a server we could understand, or came back unusable. */
export class SyncTransportError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'SyncTransportError';
    this.status = status;
  }
}

/** A server answered, and said something this client is not willing to act on. */
export class SyncProtocolError extends SyncTransportError {
  constructor(message: string) {
    super(message);
    this.name = 'SyncProtocolError';
  }
}

function readProtocolVersion(body: Record<string, unknown>): number {
  const version = body['protocolVersion'];
  if (version !== PROTOCOL_VERSION) {
    // Refusing to interpret an envelope we do not speak. ADR-0001 §4.8 keeps old clients
    // working by never breaking this shape; if it breaks anyway, an old phone in the field
    // stops and queues rather than guessing at the new meaning of a field.
    throw new SyncProtocolError(
      `sync response protocolVersion ${String(version)} is not ${String(PROTOCOL_VERSION)}`,
    );
  }
  return PROTOCOL_VERSION;
}

function readArray(body: Record<string, unknown>, key: string): readonly unknown[] {
  const value = body[key];
  if (!Array.isArray(value)) {
    throw new SyncProtocolError(`sync response field "${key}" is not an array`);
  }
  return value;
}

/**
 * Validate one per-op outcome, or refuse the whole response.
 *
 * Throwing on a single bad entry discards the good entries alongside it, which sounds
 * wasteful and is the correct trade. The ops in a rejected response stay pending and go out
 * again; `opId` dedupes the ones that already landed. Salvaging the parseable entries would
 * mean trusting a server that just proved it produces malformed results, on exactly the
 * decision that permanently releases our copy of somebody's evidence.
 */
function parseOpResult(entry: unknown, index: number): OpResult {
  const at = `results[${String(index)}]`;
  if (typeof entry !== 'object' || entry === null) {
    throw new SyncProtocolError(`${at} is not an object`);
  }

  const record = entry as Record<string, unknown>;
  const opId = record['opId'];
  if (typeof opId !== 'string' || opId.length === 0) {
    throw new SyncProtocolError(`${at} has no opId`);
  }

  const status = record['status'];
  switch (status) {
    case 'applied':
    case 'duplicate': {
      const seq = record['seq'];
      if (typeof seq !== 'number' || !Number.isFinite(seq)) {
        throw new SyncProtocolError(`${at} is "${status}" but carries no server seq`);
      }
      return { opId: opId as Uuid, status, seq };
    }
    case 'rejected': {
      return { opId: opId as Uuid, status: 'rejected', error: parseOpError(record['error'], at) };
    }
    default:
      throw new SyncProtocolError(`${at} has unknown status ${JSON.stringify(status)}`);
  }
}

const ERROR_CODES: ReadonlySet<string> = new Set<OpErrorCode>([
  'validation_failed',
  'schema_version_unsupported',
  'entity_not_found',
  'forbidden',
  'org_mismatch',
  'conflict',
  'internal',
]);

/**
 * Read a rejection, defaulting the unknown case to retryable.
 *
 * An unrecognised error code is most likely a newer server talking to an older phone. The
 * safe reading of "I do not know what this means" is that the op is still good and should
 * be sent again — a terminal reading would quarantine the entity and strand real evidence
 * on a device over a vocabulary mismatch.
 */
function parseOpError(value: unknown, at: string): OpError {
  if (typeof value !== 'object' || value === null) {
    throw new SyncProtocolError(`${at} is "rejected" but carries no error`);
  }

  const record = value as Record<string, unknown>;
  const rawCode = record['code'];
  const code: OpErrorCode = ERROR_CODES.has(rawCode as string)
    ? (rawCode as OpErrorCode)
    : 'internal';
  const message = typeof record['message'] === 'string' ? record['message'] : 'rejected';
  // Only an explicit `false` is taken as terminal. A missing flag means the server did not
  // tell us, and "resend it" is the recoverable direction to be wrong in.
  const retryable = record['retryable'] !== false;

  return { code, message, retryable };
}

/**
 * Where the API lives, or nothing.
 *
 * `undefined` is a first-class, expected answer. ADR-0002 §2 deferred API hosting rather
 * than choosing it, so this build ships with no server to sync to, and the app is required
 * to say so plainly instead of showing a spinner that will never resolve. The UI reads this
 * as the `unconfigured` state.
 */
export function resolveApiBaseUrl(
  env: Readonly<Record<string, string | undefined>>,
): string | undefined {
  const raw = env['VITE_API_BASE_URL']?.trim();
  if (raw === undefined || raw === '') return undefined;
  return raw.replace(/\/+$/, '');
}
